// src/features/stock-info/components/GradesConsensusChart.tsx
//
// Component Island: analyst Grades Consensus. Renders the Wall-Street rating
// distribution (Strong Buy / Buy / Hold / Sell / Strong Sell) as a vertical
// bar chart. The data is already fetched as part of the page-level Overview
// call (GET /stock-info/{symbol}/overview -> grades), so this component takes
// the grades object as a prop rather than firing its own request — zero extra
// network cost and instant render.

import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { StockOverview } from '../api';

// Ordered bullish -> bearish so the chart reads like a sentiment gradient.
const GRADE_ORDER: {
    key: keyof StockOverview['grades'];
    label: string;
    color: string;
}[] = [
    { key: 'strong_buy', label: 'Strong Buy', color: '#059669' },
    { key: 'buy', label: 'Buy', color: '#10b981' },
    { key: 'hold', label: 'Hold', color: '#f59e0b' },
    { key: 'sell', label: 'Sell', color: '#f97316' },
    { key: 'strong_sell', label: 'Strong Sell', color: '#ef4444' },
];

export const GradesConsensusChart = ({
    grades,
}: {
    grades: StockOverview['grades'];
}) => {
    const chartData = GRADE_ORDER.map((g) => ({
        label: g.label,
        count: typeof grades?.[g.key] === 'number' ? (grades[g.key] as number) : 0,
        color: g.color,
    }));

    const totalRatings = chartData.reduce((sum, d) => sum + d.count, 0);

    return (
        <div className="surface-card border-t-4 border-amber-500 flex flex-col h-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                <h2 className="section-title mb-0">Analyst Ratings Summary</h2>
                {grades?.consensus && (
                    <span className="px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-bold uppercase tracking-wider">
                        {grades.consensus}
                    </span>
                )}
            </div>

            <div className="relative h-[300px] w-full">
                {totalRatings === 0 ? (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                        No analyst ratings available.
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={chartData}
                            margin={{ left: -20, top: 16, right: 0, bottom: 0 }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="#334155"
                                opacity={0.3}
                            />
                            <XAxis
                                dataKey="label"
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                            />
                            <YAxis
                                allowDecimals={false}
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip
                                cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }}
                                contentStyle={{
                                    backgroundColor: '#ffffff',
                                    borderColor: '#1e293b',
                                    borderWidth: '2px',
                                    borderRadius: '8px',
                                    color: '#0f172a',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                }}
                                labelStyle={{
                                    color: '#475569',
                                    marginBottom: '4px',
                                    fontWeight: '600',
                                }}
                                itemStyle={{ color: '#0f172a', fontWeight: '700' }}
                                formatter={(value: number | string) => [
                                    `${value} analyst${value === 1 ? '' : 's'}`,
                                    'Ratings',
                                ]}
                            />
                            <Bar
                                dataKey="count"
                                radius={[4, 4, 0, 0]}
                                isAnimationActive={false}
                            >
                                <LabelList
                                    dataKey="count"
                                    position="top"
                                    style={{ fill: '#94a3b8', fontSize: 12, fontWeight: 700 }}
                                />
                                {chartData.map((entry) => (
                                    <Cell key={entry.label} fill={entry.color} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>

            {totalRatings > 0 && (
                <p className="mt-3 text-xs text-slate-500 dark:text-slate-400 text-center">
                    Based on {totalRatings} analyst rating{totalRatings === 1 ? '' : 's'}
                </p>
            )}
        </div>
    );
};

export default GradesConsensusChart;
